// Ruta: frontend/src/core/context/ChecklistContext.tsx
import React, { createContext, useContext, useState, ReactNode } from 'react';
import { Checklist, CreateChecklistPayload } from '@core/types';
import { checklistService } from '@core/services/checklistService';

interface ChecklistContextType {
  checklists: Checklist[];
  isLoading: boolean;
  loadChecklists: () => Promise<void>;
  createChecklist: (payload: CreateChecklistPayload) => Promise<Checklist>;
  updateChecklist: (id: string, payload: Partial<CreateChecklistPayload>) => Promise<Checklist>;
  deleteChecklist: (id: string) => Promise<void>;
  getChecklistById: (id: string) => Promise<Checklist>;
  getChecklistByQR: (qrCode: string) => Promise<Checklist>;
}

const ChecklistContext = createContext<ChecklistContextType | undefined>(undefined);

export const ChecklistProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [checklists, setChecklists] = useState<Checklist[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const loadChecklists = async () => {
    setIsLoading(true);
    try {
      const data = await checklistService.getAll();
      setChecklists(data || []);
    } catch (error) {
      console.error('Error cargando checklists:', error);
      setChecklists([]);
    } finally {
      setIsLoading(false);
    }
  };

  const createChecklist = async (payload: CreateChecklistPayload) => {
    const newChecklist = await checklistService.create(payload);
    setChecklists((prev) => [...prev, newChecklist]);
    return newChecklist;
  };

  const updateChecklist = async (id: string, payload: Partial<CreateChecklistPayload>) => {
    const updated = await checklistService.update({ id, ...payload });
    setChecklists((prev) => prev.map((c) => (c.id === id ? updated : c)));
    return updated;
  };

  const deleteChecklist = async (id: string) => {
    await checklistService.delete(id);
    setChecklists((prev) => prev.filter((c) => c.id !== id));
  };

  const getChecklistById = async (id: string) => {
    return checklistService.getById(id);
  };

  const getChecklistByQR = async (qrCode: string) => {
    const result = await checklistService.getByQR(qrCode);
    return result.checklist;
  };

  return (
    <ChecklistContext.Provider
      value={{
        checklists,
        isLoading,
        loadChecklists,
        createChecklist,
        updateChecklist,
        deleteChecklist,
        getChecklistById,
        getChecklistByQR,
      }}
    >
      {children}
    </ChecklistContext.Provider>
  );
};

export const useChecklist = (): ChecklistContextType => {
  const context = useContext(ChecklistContext);
  if (!context) {
    throw new Error('useChecklist must be used within a ChecklistProvider');
  }
  return context;
};